/**
 * The site's web-entry binding (ADR-0030): compose the neutral `@vello/web-entry` kernel with the site's
 * country registry to resolve, for one anonymous request, the market + language to present, the SEO metadata,
 * and whether to suggest a soft redirect. Pure and framework-neutral (testable without a DOM); the React layer
 * (`web/`) renders whatever this returns. Neutral: every country/locale comes from the registry + inputs, never
 * a literal here.
 */
import {
  resolveEntryCountry,
  resolveEntryLocale,
  parseEntryPath,
  parseGeoCountry,
  suggestEntryRedirect,
  hreflangAlternates,
  canonicalPath,
  type CountrySource,
  type LocaleSource,
  type HreflangAlternate,
  type RedirectSuggestion,
  type CountryLocaleConfig,
} from '@vello/web-entry';
import { isSupportedCountry, countryLocales, type SiteRegistry } from './registry.js';

export interface SiteEntryInputs {
  /** The pathname the visitor arrived on (`/{country}/{locale}/…`). */
  readonly pathname: string;
  /** The raw edge country header (`cf-ipcountry` / equivalent) — a hint only. */
  readonly geoCountryHeader?: string | null;
  /** A previously persisted country choice (cookie / localStorage). */
  readonly savedCountry?: string;
  /** A previously persisted locale choice. */
  readonly savedLocale?: string;
  /** A country the visitor just chose in the UI (the market switcher) — the strongest country signal. */
  readonly explicitCountry?: string;
  /** A language the visitor just chose in the UI — the strongest locale signal. */
  readonly explicitLocale?: string;
  /** The request `Accept-Language` (or `navigator.languages` joined). */
  readonly acceptLanguage?: string | null;
  /** The user-agent — a crawler is never redirected. */
  readonly userAgent?: string | null;
  /** The platform's global fallback market (a neutral default, caller-supplied — never hardcoded here). */
  readonly fallbackCountry: string;
}

export interface SiteEntry {
  readonly country: string;
  readonly countrySource: CountrySource;
  /** Whether the resolved country is a served market — the site renders it, or a "not available" browse state. */
  readonly countrySupported: boolean;
  readonly locale: string;
  readonly localeSource: LocaleSource;
  readonly localeSupportedByPack: boolean;
  /** The canonical self-URL for the resolved market+language. */
  readonly canonical: string;
  /** `hreflang` alternates for the resolved country (empty when the country is unsupported). */
  readonly hreflang: readonly HreflangAlternate[];
  /** Whether to suggest a soft, overridable redirect to the localized entry (never for a crawler/deep link). */
  readonly redirect: RedirectSuggestion;
}

/** The neutral pack used when the resolved country is unsupported — forces the locale chain to a neutral `und`. */
const NEUTRAL_PACK: CountryLocaleConfig = { defaultLocale: '', supportedLocales: [] };

/**
 * Resolve the full web entry for one request. Country and locale resolve on independent chains
 * (`Country != Locale`, ADR-0032 D3): the URL's country/locale segments act as the addressed page (so a
 * shared/deep link is respected), a fresh UI choice overrides them, and geo is only a hint. The locale chain
 * uses the resolved country's pack; an unsupported country yields a neutral locale and an empty hreflang set.
 */
export function resolveSiteEntry(reg: SiteRegistry, inputs: SiteEntryInputs): SiteEntry {
  const path = parseEntryPath(inputs.pathname, {
    isCountry: (s) => isSupportedCountry(reg, s),
  });

  const country = resolveEntryCountry({
    // A fresh UI choice wins; otherwise the addressed URL country; then a saved cookie; then the geo hint.
    explicit: inputs.explicitCountry ?? path.country,
    saved: inputs.savedCountry,
    geoCountry: parseGeoCountry(inputs.geoCountryHeader),
    fallback: inputs.fallbackCountry,
    isSupported: (cc) => isSupportedCountry(reg, cc),
  });

  const pack = countryLocales(reg, country.country) ?? NEUTRAL_PACK;
  const locale = resolveEntryLocale({
    explicit: inputs.explicitLocale,
    saved: inputs.savedLocale,
    urlLocale: path.locale,
    accountLocale: undefined,
    acceptLanguage: inputs.acceptLanguage,
    pack,
  });

  const rest = path.rest;
  return {
    country: country.country,
    countrySource: country.source,
    countrySupported: country.supported,
    locale: locale.locale,
    localeSource: locale.source,
    localeSupportedByPack: locale.supportedByPack,
    canonical: canonicalPath(country.country, locale.locale, rest),
    hreflang: country.supported ? hreflangAlternates(country.country, pack, rest) : [],
    redirect: suggestEntryRedirect({
      pathname: inputs.pathname,
      country: country.country,
      locale: locale.locale,
      userAgent: inputs.userAgent,
      known: { isCountry: (s) => isSupportedCountry(reg, s) },
    }),
  };
}
